import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiClientError } from '../api/http'
import {
  completeSallaOAuthCallback,
  getLatestChannelOnboardingJob,
  retryChannelOnboardingJob,
} from '../channels/channelsApi'
import type { ChannelOnboardingJob, ChannelOnboardingJobStatus, ChannelOnboardingStepStatus } from '../channels/types'
import { clearSallaOAuthCallback, readSallaOAuthCallbackParams, type SallaOAuthCallbackParams } from '../channels/sallaOAuthSession'
import { Button } from '../components/ui/Button'
import { Pill } from '../components/ui/Pill'
import { Icon, I } from '../components/ui/icons'
import { useIsMobileViewport } from '../hooks/useIsMobileViewport'
import { Card, ChannelDot, gridColumns, mobileButtonStyle, TEXT_WRAP_STYLE, MOBILE_FULL_WIDTH_BUTTON_STYLE } from '../components/ui'
import { TlaaqLogo } from '../components/brand/TlaaqLogo'
import { LocaleSwitcher } from '../components/locale/LocaleSwitcher'
import { OnboardingStockOwnerChoice } from '../channels/OnboardingStockOwnerChoice'

type Phase = 'submitting' | 'success' | 'error'
type PillTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'brand' | 'coral'

interface SallaOAuthPageProps {
  accessToken: string
  onDone: () => void
  /** Invitation confirmation already connected this channel; never exchange OAuth again. */
  connectedConnectionId?: number
}

const ACTIVE_JOB_STATUSES: ChannelOnboardingJobStatus[] = ['QUEUED', 'RUNNING']

function errorMessageKey(error: unknown): string {
  if (error instanceof ApiClientError) {
    return `pages.sallaOAuth.errors.codes.${error.code}`
  }
  return 'pages.sallaOAuth.errors.codes.REQUEST_FAILED'
}

function isJobActive(job: ChannelOnboardingJob | null): boolean {
  return job !== null && ACTIVE_JOB_STATUSES.includes(job.status)
}

function statusTone(status: ChannelOnboardingJobStatus): PillTone {
  if (status === 'COMPLETED') return 'success'
  if (status === 'COMPLETED_WITH_ISSUES') return 'warning'
  if (status === 'FAILED') return 'danger'
  return 'info'
}

function stepTone(status: ChannelOnboardingStepStatus): PillTone {
  if (status === 'SUCCEEDED') return 'success'
  if (status === 'SUCCEEDED_WITH_ISSUES') return 'warning'
  if (status === 'FAILED') return 'danger'
  if (status === 'RUNNING') return 'info'
  return 'neutral'
}

function isMissingJobError(error: unknown): boolean {
  return error instanceof ApiClientError && error.code === 'CHANNEL_ONBOARDING_JOB_NOT_FOUND'
}

function stripCallbackQuery(): void {
  if (typeof window !== 'undefined') {
    window.history.replaceState({}, '', '/salla-oauth')
  }
}

function clearStoredCallback(): void {
  clearSallaOAuthCallback()
  stripCallbackQuery()
}

function shouldClearAfterBackendError(error: unknown): boolean {
  if (!(error instanceof ApiClientError)) return false
  return [
    'CHANNEL_OAUTH_STATE_INVALID',
    'CHANNEL_OAUTH_CALLBACK_FAILED',
    'CHANNEL_EXTERNAL_ACCOUNT_CONFLICT',
    'CHANNEL_CONNECTION_NOT_FOUND',
  ].includes(error.code)
}

function JobStep({ label, status }: { label: string; status: ChannelOnboardingStepStatus }) {
  const { t } = useTranslation()
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 'var(--sp-3)', alignItems: 'center', padding: 'var(--sp-3)', borderRadius: 'var(--r-md)', background: 'var(--ink-50)', border: '1px solid var(--border-subtle)' }}>
      <span style={{ fontSize: 'var(--fs-14)', fontWeight: 'var(--fw-extra)', color: 'var(--fg)' }}>{label}</span>
      <Pill tone={stepTone(status)} size="sm" dot>
        {t(`pages.sallaOAuth.import.stepStatus.${status}`)}
      </Pill>
    </div>
  )
}

function JobProgress({
  job,
  jobError,
  retrying,
  onRetry,
}: {
  job: ChannelOnboardingJob | null
  jobError: unknown
  retrying: boolean
  onRetry: () => void
}) {
  const { t } = useTranslation()
  const isMobile = useIsMobileViewport()

  if (!job) {
    return (
      <div style={{ padding: 'var(--sp-4)', borderRadius: 'var(--r-md)', background: 'var(--brand-100)', border: '1px solid var(--brand-300)', textAlign: 'start' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 'var(--sp-3)', alignItems: 'center' }}>
          <div style={{ fontSize: 'var(--fs-16)', fontWeight: 'var(--fw-extra)', color: 'var(--brand-900)' }}>{t('pages.sallaOAuth.import.queuedTitle')}</div>
          <Pill tone="info" size="sm" dot>{t('pages.sallaOAuth.import.status.QUEUED')}</Pill>
        </div>
        <div style={{ fontSize: 'var(--fs-14)', color: 'var(--fg-muted)', marginTop: 'var(--sp-2)', lineHeight: 'var(--lh-normal)' }}>
          {t('pages.sallaOAuth.import.queuedBody')}
        </div>
        {jobError !== null && !isMissingJobError(jobError) && <p role="status" style={{ color: 'var(--warning)', fontSize: 'var(--fs-14)' }}>{t('pages.sallaOAuth.import.refreshFailed')}</p>}
      </div>
    )
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-3)', textAlign: 'start', minWidth: 0 }}>
      <div style={{ padding: 'var(--sp-4)', borderRadius: 'var(--r-md)', background: 'var(--ink-50)', border: '1px solid var(--border-subtle)', minWidth: 0 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 'var(--sp-3)', alignItems: 'center', marginBottom: 'var(--sp-3)' }}>
          <div style={{ fontSize: 'var(--fs-16)', fontWeight: 'var(--fw-extra)', color: 'var(--brand-900)' }}>{t('pages.sallaOAuth.import.title')}</div>
          <Pill tone={statusTone(job.status)} size="sm" dot>
            {t(`pages.sallaOAuth.import.status.${job.status}`)}
          </Pill>
        </div>

        <p style={{ fontSize: 'var(--fs-14)', color: 'var(--fg-muted)', margin: '0 0 var(--sp-3)', lineHeight: 'var(--lh-normal)' }}>{t('pages.sallaOAuth.import.subtitle')}</p>
        <div style={{ display: 'grid', gap: 'var(--sp-3)' }}>
          <JobStep label={t('pages.sallaOAuth.import.products')} status={job.productStatus} />
          <JobStep label={t('pages.sallaOAuth.import.orders')} status={job.orderStatus} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'repeat(3, minmax(0, 1fr))'), gap: 8, marginTop: 12 }}>
          {[
            ['products', job.productUpserted],
            ['stock', job.stockRowsImported],
            ['orders', job.orderUpserted],
          ].map(([key, value]) => (
            <div key={key} style={{ padding: 10, borderRadius: 10, background: 'white', border: '1px solid var(--border-subtle)' }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--brand-900)' }}>{value}</div>
              <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginTop: 1 }}>{t(`pages.sallaOAuth.import.counters.${key}`)}</div>
            </div>
          ))}
        </div>

        {job.productIssueCount > 0 && (
          <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--warning)', fontWeight: 700 }}>
            {t('pages.sallaOAuth.import.issueCount', { count: job.productIssueCount })}
          </div>
        )}

        {job.errorCode && (
          <div role="alert" style={{ marginTop: 'var(--sp-3)', padding: 'var(--sp-3)', borderRadius: 'var(--r-sm)', background: 'var(--danger-100)', color: 'var(--danger)', fontSize: 'var(--fs-14)', lineHeight: 'var(--lh-normal)' }}>
            {t(`pages.sallaOAuth.import.errors.${job.errorCode}`, { defaultValue: t('pages.sallaOAuth.import.errors.generic') })}
          </div>
        )}

        {job.status === 'FAILED' && (
          <Button kind="coral" size="sm" icon={I.refresh} style={{ marginTop: 14, ...mobileButtonStyle(isMobile) }} onClick={onRetry} disabled={retrying}>
            {retrying ? t('pages.sallaOAuth.import.retrying') : t('pages.sallaOAuth.import.retry')}
          </Button>
        )}
      </div>

      {jobError !== null && !isMissingJobError(jobError) && (
        <div style={{ fontSize: 12.5, color: 'var(--warning)', fontWeight: 700 }}>
          {t('pages.sallaOAuth.import.refreshFailed')}
        </div>
      )}
    </div>
  )
}

export function SallaOAuthPage({ accessToken, onDone, connectedConnectionId }: SallaOAuthPageProps) {
  const { t } = useTranslation()
  const [phase, setPhase] = useState<Phase>(connectedConnectionId ? 'success' : 'submitting')
  const [error, setError] = useState<unknown>(null)
  const [connectionId, setConnectionId] = useState<number | null>(connectedConnectionId ?? null)
  const [job, setJob] = useState<ChannelOnboardingJob | null>(null)
  const [jobError, setJobError] = useState<unknown>(null)
  const [retrying, setRetrying] = useState(false)
  const params = useMemo<SallaOAuthCallbackParams | null>(() => connectedConnectionId ? null : readSallaOAuthCallbackParams(), [connectedConnectionId])
  // Guard against double-invocation in React StrictMode dev mode — the backend's
  // one-time state would otherwise reject the second call.
  const sentRef = useRef(false)
  const isMobile = useIsMobileViewport()

  const fetchLatestJob = useCallback(async (connectionId: number) => {
    try {
      const latest = await getLatestChannelOnboardingJob(accessToken, connectionId)
      setJob(latest)
      setJobError(null)
    } catch (caught) {
      if (isMissingJobError(caught)) {
        setJobError(null)
        return
      }
      setJobError(caught)
    }
  }, [accessToken])

  useEffect(() => {
    if (sentRef.current) return
    sentRef.current = true

    if (connectedConnectionId) {
      void Promise.resolve().then(() => fetchLatestJob(connectedConnectionId))
      return
    }
    if (!params) return

    if (params.error) {
      Promise.resolve().then(() => {
        clearStoredCallback()
        setPhase('error')
        setError(new ApiClientError(400, {
          code: 'CHANNEL_OAUTH_CALLBACK_FAILED',
          message: params.errorDescription ?? params.error ?? undefined,
        }))
      })
      return
    }

    if (!params.state) {
      Promise.resolve().then(() => {
        clearStoredCallback()
        setPhase('error')
        setError(new ApiClientError(400, { code: 'CHANNEL_OAUTH_STATE_INVALID' }))
      })
      return
    }

    if (!params.code) {
      Promise.resolve().then(() => {
        clearStoredCallback()
        setPhase('error')
        setError(new ApiClientError(400, { code: 'CHANNEL_OAUTH_CALLBACK_FAILED' }))
      })
      return
    }

    completeSallaOAuthCallback(accessToken, {
      code: params.code,
      state: params.state,
      error: params.error,
      errorDescription: params.errorDescription,
    })
      .then(connected => {
        setConnectionId(connected.id)
        setPhase('success')
        clearStoredCallback()
        void fetchLatestJob(connected.id)
      })
      .catch(caught => {
        if (shouldClearAfterBackendError(caught)) {
          clearStoredCallback()
        }
        setPhase('error')
        setError(caught)
      })
  }, [accessToken, connectedConnectionId, fetchLatestJob, params])

  useEffect(() => {
    if (phase !== 'success' || !connectionId) return
    if (job && !isJobActive(job)) return

    const interval = window.setInterval(() => {
      void fetchLatestJob(connectionId)
    }, 1500)
    return () => window.clearInterval(interval)
  }, [connectionId, fetchLatestJob, job, phase])

  const handleRetry = async () => {
    if (!job) return
    setRetrying(true)
    try {
      const retried = await retryChannelOnboardingJob(accessToken, job.id)
      setJob(retried)
      setJobError(null)
    } catch (caught) {
      setJobError(caught)
    } finally {
      setRetrying(false)
    }
  }

  if (phase === 'success') {
    return <main style={{ minHeight: '100dvh', boxSizing: 'border-box', background: 'var(--bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: isMobile ? 'var(--sp-6)' : 'var(--sp-12)', padding: isMobile ? 'var(--sp-4)' : 'var(--sp-12)' }}>
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 'var(--sp-4)' }}>
        <TlaaqLogo height={30} /><LocaleSwitcher />
      </header>
      <Card style={{ width: 560, maxWidth: '100%', display: 'grid', gap: 'var(--sp-5)', padding: isMobile ? 'var(--sp-6)' : 'var(--sp-8)', ...TEXT_WRAP_STYLE }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', color: 'var(--fg-muted)', fontSize: 'var(--fs-14)' }}>
          <ChannelDot name="Salla" size={28} />{t('pages.sallaInvitation.eyebrow')}
        </div>
        <div style={{ display: 'grid', gap: 'var(--sp-3)' }}>
          <h1 style={{ margin: 0, fontSize: 'var(--fs-30)', fontWeight: 'var(--fw-bold)', lineHeight: 'var(--lh-snug)' }}>{t('pages.sallaOAuth.success.title')}</h1>
          <p style={{ margin: 0, color: 'var(--fg-muted)', fontSize: 'var(--fs-16)', lineHeight: 'var(--lh-normal)' }}>{t('pages.sallaOAuth.success.bodyShort')}</p>
        </div>
        <OnboardingStockOwnerChoice accessToken={accessToken} job={job} onJobChange={setJob} />
        <JobProgress job={job} jobError={jobError} retrying={retrying} onRetry={handleRetry} />
        <Button size="lg" style={MOBILE_FULL_WIDTH_BUTTON_STYLE} onClick={onDone}>{t('pages.sallaOAuth.success.back')}</Button>
      </Card>
    </main>
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? '20px 14px' : '32px 28px' }}>
        {phase === 'submitting' && (
          <div style={{ width: 520, maxWidth: '100%', background: 'white', borderRadius: 16, border: '1px solid var(--border-subtle)', padding: isMobile ? 24 : 36, textAlign: 'center', boxShadow: '0 12px 40px rgba(15,23,42,0.06)' }}>
            <span style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--brand-100)', color: 'var(--brand-700)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18 }}>
              <Icon size={26}>{I.refresh}</Icon>
            </span>
            <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--brand-900)', letterSpacing: '-0.015em' }}>
              {t('pages.sallaOAuth.submitting.title')}
            </div>
            <p style={{ fontSize: 14, color: 'var(--fg-muted)', marginTop: 8, lineHeight: 1.55 }}>
              {t('pages.sallaOAuth.submitting.body')}
            </p>
          </div>
        )}

        {phase === 'error' && (
          <div style={{ width: 480, maxWidth: '100%', background: 'white', borderRadius: 16, border: '1px solid var(--border-subtle)', padding: isMobile ? 24 : 36, textAlign: 'center', boxShadow: '0 12px 40px rgba(15,23,42,0.06)' }}>
            <span style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--danger-100)', color: 'var(--danger)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18 }}>
              <Icon size={26} stroke={3}>{I.alert}</Icon>
            </span>
            <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--brand-900)', letterSpacing: '-0.015em' }}>
              {t('pages.sallaOAuth.error.title')}
            </div>
            <p style={{ fontSize: 14, color: 'var(--fg-muted)', marginTop: 8, lineHeight: 1.55 }}>
              {String(t(errorMessageKey(error), { defaultValue: String(t('pages.sallaOAuth.errors.codes.REQUEST_FAILED')) }))}
            </p>
            <Button kind="secondary" size="md" icon={I.arrowR} style={{ marginTop: 22, ...mobileButtonStyle(isMobile) }} onClick={onDone}>
              {t('pages.sallaOAuth.error.back')}
            </Button>
          </div>
        )}
      </div>

      <div style={{ padding: '16px 28px', borderTop: '1px solid var(--border-subtle)', background: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 12, color: 'var(--fg-subtle)' }}>
          <Icon size={14} color="var(--fg-subtle)">{I.shield}</Icon>
          {t('pages.sallaOAuth.footer.security')}
        </div>
      </div>
    </div>
  )
}
